import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  updateDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
// Import doc separately to avoid minification issues
import { doc as firestoreDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Message } from '../types';
import config from '../utils/config';

export const useFirebaseChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionMetadata, setSessionMetadata] = useState<any>(null);
  // AI translation service is always online - no need for RTDB presence tracking
  const [serverPresence] = useState<{isOnline: boolean, lastSeen?: Date}>({isOnline: true});
  const [sessionStatus, setSessionStatus] = useState<'active' | 'ended' | 'terminated' | 'unknown'>('unknown');
  const [chatStatus, setChatStatus] = useState<'active' | 'disabled'>('active');
  const [serverData, setServerData] = useState<{displayName?: string, name?: string}>({});
  const [restaurantData, setRestaurantData] = useState<{name?: string, address?: string, city?: string, state?: string, logoUrl?: string}>({});
  
  // Guest information now managed server-side
  const [currentGuestId, setCurrentGuestId] = useState<number | null>(null);
  const currentGuestIdRef = useRef<number | null>(null); // Ref for closure access in listeners
  const [currentDisplayName, setCurrentDisplayName] = useState<string | null>(null);
  
  
  // Fun, high-contrast color palette for patron message bubbles
  const patronColors = [
    '#e91e63', // Pink (Guest 1)
    '#9c27b0', // Purple (Guest 2)
    '#673ab7', // Deep Purple (Guest 3)
    '#3f51b5', // Indigo (Guest 4)
    '#2196f3', // Blue (Guest 5)
    '#03a9f4', // Light Blue (Guest 6)
    '#00bcd4', // Cyan (Guest 7)
    '#009688', // Teal (Guest 8)
    '#4caf50', // Green (Guest 9)
    '#8bc34a', // Light Green (Guest 10)
    '#cddc39', // Lime (Guest 11)
    '#ffeb3b', // Yellow (Guest 12)
    '#ffc107', // Amber (Guest 13)
    '#ff9800', // Orange (Guest 14)
    '#ff5722', // Deep Orange (Guest 15)
    '#795548', // Brown (Guest 16)
    '#607d8b', // Blue Grey (Guest 17)
    '#f44336', // Red (Guest 18)
    '#e65100', // Dark Orange (Guest 19)
    '#1a237e', // Dark Blue (Guest 20)
  ];

  const currentSessionId = useRef<string | null>(null);
  const patronToken = useRef<string>('');
  const unsubscribeMessages = useRef<(() => void) | null>(null);
  const unsubscribePresence = useRef<(() => void) | null>(null);
  const unsubscribeSession = useRef<(() => void) | null>(null);
  const unsubscribeTable = useRef<(() => void) | null>(null);
  const unsubscribeServerPresence = useRef<(() => void) | null>(null);
  const unsubscribeRestaurant = useRef<(() => void) | null>(null);

  // Helper function to get patron color using server-assigned guest ID
  const getPatronColor = (tokenToCheck: string, guestId?: number): string => {
    // For current user, use their assigned guest ID
    if (tokenToCheck === patronToken.current && currentGuestId !== null) {
      return patronColors[(currentGuestId - 1) % patronColors.length];
    }
    
    // For other users, use provided guest ID or fallback
    const id = guestId || 1;
    return patronColors[(id - 1) % patronColors.length];
  };

  const joinSession = useCallback(async (sessionId: string, token: string, guestId?: number, displayName?: string) => {
    try {
      
      if (!token || typeof token !== 'string') {
        throw new Error(`Invalid token passed to joinSession: ${token} (type: ${typeof token})`);
      }
      
      // Set current session and token
      currentSessionId.current = sessionId;
      patronToken.current = token;
      
      // Set guest information from server
      if (guestId) {
        setCurrentGuestId(guestId);
        currentGuestIdRef.current = guestId; // Update ref for listener access
      }
      if (displayName) {
        setCurrentDisplayName(displayName);
      }

      // OPTIMIZATION: Set connected state immediately - user can start typing while listeners set up in background
      // All required data for sending messages is now available (sessionId, token, guestId)
      setIsConnected(true);
      setError(null);

      // Update patron presence
      const patronPresenceRef = firestoreDoc(db, 'sessions', sessionId, 'presence', token);
      await setDoc(patronPresenceRef, {
        userId: token,
        userType: 'patron',
        status: 'online',
        lastSeen: serverTimestamp(),
        isTyping: false,
      }, { merge: true });

      // Listen for messages
      const messagesQuery = query(
        collection(db, 'sessions', sessionId, 'messages'),
        orderBy('timestamp', 'asc')
      );

      unsubscribeMessages.current = onSnapshot(
        messagesQuery,
        (snapshot) => {
          const newMessages: Message[] = [];
          const myToken = patronToken.current;
          const myGuestId = currentGuestIdRef.current;

          snapshot.forEach((doc) => {
            const data = doc.data();
            const senderType = data.senderType || '';
            const senderId = data.senderId || '';
            const metadata = data.metadata || {};
            
            // PWC FILTER: Always hide staff instructions and link clicks
            if (metadata.isStaffInstruction === true || metadata.isLinkClick === true) {
              return;
            }

            // PWC FILTER: Private messages (promo codes) - only show to the specific guest
            if (metadata.isPrivate === true && metadata.forGuestId !== undefined && metadata.forGuestId !== myGuestId) {
              return;
            }

            // ============================================================
            // PER-PATRON ISOLATION FILTERS
            // ============================================================

            // PATRON MESSAGES: Only show my own messages
            if (senderType === 'patron') {
              if (senderId !== myToken) {
                return; // Skip other patrons' messages
              }
            }

            // AUTO-RESPONSES: Only show responses targeted at me
            if (metadata.isAutoResponse === true && metadata.forSenderId) {
              if (metadata.forSenderId !== myToken) {
                return; // Skip auto-responses for other patrons
              }
            }

            // BROADCAST MESSAGES pass through:
            // - System messages without forSenderId (welcome, daily specials, opt-in)
            // - Server/manager messages (staff broadcasts to the table)

            const guestNumber = metadata.guestNumber;
            const patronColor = metadata.patronColor;
            
            newMessages.push({
              id: doc.id,
              sessionId: data.sessionId,
              sender: senderType,
              senderName: senderType === 'server' 
                ? (data.senderDisplayName || data.senderName || 'Unknown User')
                : senderType === 'system'
                  ? (data.senderDisplayName || data.senderName || 'Guest')
                  : 'Guest',
              senderColor: senderType === 'patron' 
                ? (patronColor || getPatronColor(senderId, guestNumber))
                : undefined,
              senderRole: data.senderRole,
              content: {
                text: data.content || '',
                type: 'text'
              },
              timestamp: data.timestamp?.toDate() || new Date(),
              status: 'delivered',
              metadata: metadata
            });
          });
          
          // UUID-based deduplication: Only add messages we don't already have
          setMessages(prevMessages => {
            const newUniqueMessages = newMessages.filter(newMsg => 
              !prevMessages.some(existing => existing.id === newMsg.id)
            );
            
            const allMessages = [...prevMessages, ...newUniqueMessages];
            allMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            return allMessages;
          });
        },
        (error) => {
          console.error('Messages listener error:', error);
          setError(error.message);
          setIsConnected(false);
        }
      );


      // Listen for session metadata (server name, table number, etc.)
      const sessionDocRef = firestoreDoc(db, 'sessions', sessionId);
      unsubscribeSession.current = onSnapshot(
        sessionDocRef,
        (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            setSessionMetadata(data);
            
            // Check session status and handle termination
            const newStatus = data.status || 'active';
            const previousStatus = sessionStatus;
            setSessionStatus(newStatus);
            
            // Check chat status (disable/enable functionality)
            const newChatStatus = data.sessionStatus || 'active';
            setChatStatus(newChatStatus);
            
            // If session just ended, add a system message
            if (previousStatus === 'active' && newStatus === 'ended') {
              
              // Add system message to chat
              const systemMessage: Message = {
                id: `system_${Date.now()}`,
                sessionId: currentSessionId.current || '',
                sender: 'system',
                senderName: 'System',
                content: {
                  text: 'Chat session has ended. Your server has cleared the table.',
                  type: 'system'
                },
                timestamp: new Date(),
                status: 'delivered'
              };
              
              setMessages(prevMessages => [...prevMessages, systemMessage]);
            }
            
            // SECURITY FIX: Read server/restaurant names from session metadata (cached at session creation)
            // This avoids querying servers/restaurants/tables collections which require authentication
            if (!data.tableNumber) {
              console.warn('⚠️ Session metadata missing table number:', data);
            }

            // Extract cached data from session metadata
            const serverName = data.metadata?.serverName || 'Server';
            const restaurantName = data.metadata?.restaurantName || 'Restaurant';
            const serverId = data.metadata?.serverId || data.serverId; // serverId for presence lookup
            
            console.log('✅ Using cached session metadata:', { serverName, restaurantName, serverId });
            
            // Update server data with cached name (real-time updates via session listener)
            setServerData({
              displayName: serverName,
              name: serverName
            });

            // AI translation service is always online - no RTDB presence tracking needed
            console.log('✅ AI translation service ready - always online');

            // SECURITY FIX: Use cached restaurant data from session metadata
            const restaurantAddress = data.metadata?.restaurantAddress || '';
            const restaurantCity = data.metadata?.restaurantCity || '';
            const restaurantState = data.metadata?.restaurantState || '';
            const restaurantLogoUrl = data.metadata?.restaurantLogoUrl || '';
            
            setRestaurantData({
              name: restaurantName,
              address: restaurantAddress,
              city: restaurantCity,
              state: restaurantState,
              logoUrl: restaurantLogoUrl
            });
            
            console.log('✅ Restaurant data set from session metadata:', { 
              name: restaurantName, 
              address: restaurantAddress,
              logoUrl: restaurantLogoUrl 
            });
          } else {
            console.error('❌ Session document does not exist:', sessionId);
            setError('Session not found in database');
          }
        },
        (error) => {
          console.error('❌ Session metadata listener error:', error);
          setError(`Failed to load session data: ${error.message}`);
        }
      );

      // Listeners are now set up in background - connection state already set to true above

    } catch (error) {
      console.error('Error joining session:', error);
      setError(error instanceof Error ? error.message : 'Failed to join session');
      setIsConnected(false);
    }
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!currentSessionId.current || !patronToken.current) {
      throw new Error('Not connected to session');
    }

    if (!currentGuestId) {
      throw new Error('Cannot send message: Guest ID not received from Firebase yet');
    }
    
    const patronColor = getPatronColor(patronToken.current, currentGuestId);
    const guestNumber = currentGuestId;
    
    // Generate UUID for this message (industry standard for deduplication)
    const messageId = crypto.randomUUID();
    
    // Optimistic UI: Show message immediately with what I typed
    const optimisticMessage: Message = {
      id: messageId, // Use UUID instead of optimistic_ prefix
      sessionId: currentSessionId.current,
      sender: 'patron',
      senderName: 'Guest', // Hide guest numbers for cleaner UI
      senderColor: patronColor,
      content: {
        text: text, // Show original text I typed
        type: 'text'
      },
      timestamp: new Date(),
      status: 'sent'
    };
    
    // Add immediately for instant feedback
    setMessages(prevMessages => [...prevMessages, optimisticMessage]);
    
    // Fire-and-forget: Send to GPT relay in background (don't wait)
    const relayUrl = config.gptRelayUrl + '/api/relay/message';
    
    fetch(relayUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messageId: messageId, // Send UUID to relay for deduplication
        sessionId: currentSessionId.current,
        senderType: 'patron',
        senderId: patronToken.current,
        content: text,
        messageType: 'text',
        metadata: {
          patronColor: patronColor,
          guestNumber: guestNumber
        }
      })
    }).then(async (response) => {
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Message relayed through GPT:', result);
      } else {
        console.error('❌ GPT relay error:', response.status);
      }
    }).catch((error) => {
      console.error('❌ Background relay error:', error);
    });

    // Return immediately - optimistic message already visible
  }, [currentGuestId, currentDisplayName]);


  // Log link click event (for reporting - silent, no UI)
  const logLinkClick = useCallback(async (url: string) => {
    if (!currentSessionId.current) {
      console.warn('Cannot log link click: no active session');
      return;
    }

    try {
      const clickId = crypto.randomUUID();
      const messageRef = firestoreDoc(db, 'sessions', currentSessionId.current, 'messages', clickId);
      
      await setDoc(messageRef, {
        id: clickId,
        sessionId: currentSessionId.current,
        senderId: 'system',
        senderType: 'system',
        senderName: 'System',
        content: `Link clicked: ${url}`,
        messageType: 'text',
        timestamp: serverTimestamp(),
        metadata: {
          isLinkClick: true,
          url: url,
          guestId: currentGuestId
        }
      });

      console.log('✅ Link click logged:', url);
    } catch (error) {
      // Silent fail - don't disrupt user experience for analytics
      console.warn('Failed to log link click:', error);
    }
  }, [currentGuestId]);

  // Send OTP verification code (Step 1 of opt-in)
  const sendOptInCode = useCallback(async (data: { email?: string; phoneNumber?: string }) => {
    if (!currentSessionId.current || !sessionMetadata) {
      throw new Error('Not connected to session');
    }

    try {
      const response = await fetch('https://gpt-chat-relay-625915153805.us-central1.run.app/api/opt-in/send-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: currentSessionId.current,
          restaurantId: sessionMetadata.restaurantId,
          email: data.email,
          phoneNumber: data.phoneNumber,
          consentPolicyVersion: '2026-02-26',
          consentUserAgent: navigator.userAgent
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ Send code failed:', result);
        throw new Error(result.error || 'Failed to send verification code');
      }

      console.log('✅ Verification code sent:', result);
      return result;
    } catch (error) {
      console.error('❌ Send code error:', error);
      throw error;
    }
  }, [sessionMetadata]);

  // Verify OTP code and get promo code (Step 2 of opt-in)
  const verifyOptInCode = useCallback(async (data: { email?: string; phoneNumber?: string; code: string }) => {
    if (!currentSessionId.current || !sessionMetadata) {
      throw new Error('Not connected to session');
    }

    try {
      const response = await fetch('https://gpt-chat-relay-625915153805.us-central1.run.app/api/opt-in/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: currentSessionId.current,
          restaurantId: sessionMetadata.restaurantId,
          guestId: currentGuestId,
          email: data.email,
          phoneNumber: data.phoneNumber,
          code: data.code,
          tableNumber: sessionMetadata.tableNumber,
          consentPolicyVersion: '2026-02-26',
          consentUserAgent: navigator.userAgent
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ Verify code failed:', result);
        throw new Error(result.error || 'Verification failed');
      }

      console.log('✅ Verification successful:', result);
      // The promo code message will be received via the Firestore messages listener
      return result;
    } catch (error) {
      console.error('❌ Verify code error:', error);
      throw error;
    }
  }, [sessionMetadata, currentGuestId]);

  const leaveSession = useCallback(async () => {
    if (!currentSessionId.current || !patronToken.current) return;

    try {
      // Update presence to offline
      const patronPresenceDocRef = firestoreDoc(db, 'sessions', currentSessionId.current, 'presence', patronToken.current);
      await updateDoc(
        patronPresenceDocRef,
        {
          status: 'offline',
          lastSeen: serverTimestamp(),
          isTyping: false,
        }
      );

      // Unsubscribe from listeners
      unsubscribeMessages.current?.();
      unsubscribePresence.current?.();
      unsubscribeSession.current?.();
      unsubscribeTable.current?.();
      unsubscribeServerPresence.current?.();
      unsubscribeRestaurant.current?.();

      currentSessionId.current = null;
      patronToken.current = '';
      setIsConnected(false);
      setMessages([]);
      setSessionMetadata(null);
      setServerData({});
      setRestaurantData({});
      
      // Reset guest information when leaving session
      setCurrentGuestId(null);
      setCurrentDisplayName(null);

    } catch (error) {
      console.error('Error leaving session:', error);
    }
  }, []);

  // No longer need RTDB connection monitoring - AI service is always available

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      leaveSession();
    };
  }, [leaveSession]);

  return {
    messages,
    isConnected,
    error,
    sessionMetadata,
    serverPresence,
    serverData,
    restaurantData,
    sessionStatus,
    chatStatus,
    guestId: currentGuestId,
    joinSession,
    sendMessage,
    leaveSession,
    logLinkClick,
    sendOptInCode,
    verifyOptInCode,
  };
};